import { Dialog } from '@angular/cdk/dialog';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import {
  CasualTournamentDetails,
  CasualTournamentEntity,
  EventSchedule,
  Game,
  Metadata,
  PrizeDetails,
  TournamentPass,
} from '@fantaz/shared/data-access/models';
import { TimeComponents } from '@fantaz/shared/utils/date';
import { DialogVideoComponent } from '@fantaz/tournaments/ui/dialog-video';
// !FIXME: remove relative path
import { CurrentUser } from '@fantaz/shared/data-access/models';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable, Subject, Subscription, fromEvent } from 'rxjs';

interface ActionBarConfig {
  showJoinButton?: boolean;
  showPlayButton?: boolean;
  enabledPlayButton?: boolean | null | undefined;
  playButtonLoading?: boolean | null | undefined;
  showTryForFreeButton?: boolean;
  showShareButton?: boolean;
  showPlayButtonAnother?: boolean;
  isGameOverPage?: boolean;
  isEventPage?: boolean;
}

@UntilDestroy()
@Component({
  selector: 'fz-banner',
  templateUrl: './banner.component.html',
  styleUrls: ['./banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BannerComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('banner', { static: true })
  bannerEl: ElementRef;

  @Input()
  actionBarConfig?: ActionBarConfig;

  @Input()
  user: CurrentUser | null | undefined;

  @Input()
  playText?: string;

  @Input()
  sticky = false;

  @Input()
  tournament?: CasualTournamentEntity | undefined;

  @Input()
  tournamentDetails?: CasualTournamentDetails | undefined | null;

  @Input()
  gameDetails?: Game | undefined | null;

  @Input()
  eventScheduler?: EventSchedule | undefined | null;

  @Input()
  variant?: string = 'banner';

  @Input()
  isGameHistoryDetails = false;

  @Input()
  passForTournament?: TournamentPass | null;

  @Input()
  assetUrl? = '';

  @Input()
  minHeight = 100;

  @Input()
  ratio = 2;

  @Input()
  timeZone?: string | null;

  @Input()
  startPosFix = 36;

  @Output()
  joinPress = new EventEmitter();

  @Output()
  playPress: EventEmitter<TournamentPass | null> =
    new EventEmitter<TournamentPass | null>();

  @Output()
  tryForFreePress = new EventEmitter();

  @Output()
  sharePress = new EventEmitter();

  @Output()
  playAnotherPress = new EventEmitter();

  private mediaSubscription: Subscription;
  unsubscribe$: Subject<void> = new Subject();
  customBreakpoint = '';
  initialHeight = 0;
  initialTop = 116;

  public countdown$: Observable<TimeComponents>;

  constructor(
    private breakpointObserver: BreakpointObserver,
    private changeDetector: ChangeDetectorRef,
    private dialogService: Dialog,
    private renderer: Renderer2,
    private bannerComponentEl: ElementRef,
  ) {}

  ngOnDestroy(): void {
    // this.mediaSubscription.unsubscribe();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngOnInit(): void {
    // this.mediaSubscription = this.mediaObserver
    //   .asObservable()
    //   .subscribe((change) => {
    //     this.mediaQuery = change[0];
    //     this.changeDetector.markForCheck();
    //   });

    this.breakpointObserver
      .observe(['(max-width: 599px)'])
      .subscribe((result: BreakpointState) =>
        this.updateInitialTop(result.matches),
      );

    this.breakpointObserver
      .observe(['(min-width: 960px) and (max-width: 992px)'])
      .subscribe((result: BreakpointState) => {
        this.customBreakpoint = '';
        Object.keys(result.breakpoints).forEach((breakpoint) => {
          const matched = result.breakpoints[breakpoint];
          if (
            matched &&
            breakpoint === '(min-width: 960px) and (max-width: 992px)'
          ) {
            this.customBreakpoint = 'bt-960-992';
          }
        });
        this.changeDetector.markForCheck();
      });
  }

  ngAfterViewInit() {
    if (this.sticky) {
      this.onWindowScroll();
      this.initialHeight = this.bannerEl.nativeElement.offsetHeight;
    }
  }

  updateInitialTop(matches: boolean) {
    const cssStyleDeclaration = getComputedStyle(document.body);
    const toolbarHeight =
      +cssStyleDeclaration
        .getPropertyValue('--toolbar-height')
        .replace('px', '') || 80;
    const toolbarHeightMobile =
      +cssStyleDeclaration
        .getPropertyValue('--toolbar-height-mobile')
        .replace('px', '') || 56;
    const breadcrumbHeight =
      +cssStyleDeclaration
        .getPropertyValue('--breadcrumb-height')
        .replace('px', '') || 36;

    this.initialTop = matches
      ? breadcrumbHeight + toolbarHeightMobile
      : breadcrumbHeight + toolbarHeight;
  }

  onWindowScroll() {
    fromEvent(window, 'scroll')
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        const scrollY =
          window.pageYOffset || document.documentElement.scrollTop;

        if (
          scrollY &&
          !document.activeElement?.className.includes('share-button')
        ) {
          this.updateBannerHeight(scrollY);
        }
      });
  }

  updateBannerHeight(scrollY: number) {
    const isSticky = scrollY > this.startPosFix;

    const val = Math.ceil(this.initialHeight - scrollY * this.ratio);
    const height = val > this.minHeight ? val + 'px' : this.minHeight + 'px';

    if (scrollY > this.startPosFix) {
      this.renderer.addClass(this.bannerEl.nativeElement, 'banner-fadeout');
    } else {
      this.renderer.removeClass(this.bannerEl.nativeElement, 'banner-fadeout');
    }

    if (isSticky) {
      this.renderer.addClass(this.bannerComponentEl.nativeElement, 'sticky');
      this.renderer.addClass(this.bannerEl.nativeElement, 'sticky');
      this.renderer.addClass(this.bannerEl.nativeElement, 'sticky');
      this.renderer.addClass(this.bannerEl.nativeElement, 'banner-sticky');
      this.renderer.setStyle(
        this.bannerEl.nativeElement,
        'top',
        this.initialTop + 'px',
      );
      this.renderer.setStyle(this.bannerEl.nativeElement, 'height', height);
    } else {
      this.renderer.setStyle(this.bannerEl.nativeElement, 'top', 0);
      this.renderer.removeClass(this.bannerComponentEl.nativeElement, 'sticky');
      this.renderer.removeClass(this.bannerEl.nativeElement, 'sticky');
      this.renderer.removeClass(this.bannerEl.nativeElement, 'banner-sticky');
      this.renderer.setStyle(this.bannerEl.nativeElement, 'height', '');
    }
  }

  /*
   * Action button visibility
   */

  get prizeImageOrientation(): 'landscape' | 'portrait' {
    if (this.prizeDetail && this.prizeDetail.prizeImageLayout) {
      return this.prizeDetail.prizeImageLayout === 'landscape'
        ? 'landscape'
        : 'portrait';
    }
    return 'portrait';
  }

  get isCompleted(): boolean {
    return (
      (this.tournament && this.tournament.isComplete) ||
      (this.tournamentDetails && this.tournamentDetails.isCompleted) ||
      false
    );
  }

  get isTryForFreeEnabled(): boolean {
    return this.actionBarConfig?.showTryForFreeButton ?? false;
  }

  private getDefaultImage(
    game: Game,
    type: 'avatar' | 'background' = 'avatar',
  ): string {
    const result = game?.key.split('.');
    const [gameName, version] = result || ['default', 'v1'];

    if (type === 'background') {
      return `${this.assetUrl}/shared/img/games/${gameName}/${version}/${gameName}-background.webp`;
    }

    return `${this.assetUrl}/shared/img/games/${gameName}/${version}/${gameName}-${type}.webp`;
  }

  get gameImageUrl(): string {
    let game;
    if (this.tournament) {
      game = this.tournament.game;
    } else if (this.tournamentDetails) {
      game = this.tournamentDetails?.game;
    } else {
      game = this.gameDetails;
    }

    return this.getDefaultImage(game as Game, 'avatar');
  }

  get backgroundImageUrl(): string {
    if (this.tournament) {
      if (this.tournament.tournamentFullSizeBanner) {
        return this.tournament.tournamentFullSizeBanner;
      }

      return this.getDefaultImage(this.tournament?.game, 'background');
    }

    if (this.tournamentDetails) {
      if (this.tournamentDetails.fullSizeBannerImage) {
        return this.tournamentDetails.fullSizeBannerImage as string;
      }

      return this.getDefaultImage(this.tournamentDetails?.game, 'background');
    }

    if (this.gameDetails) {
      return this.getDefaultImage(this.gameDetails, 'background');
    }

    return '';
  }

  get game(): Game | null {
    return (
      this.tournament?.game ||
      this.tournamentDetails?.game ||
      this.gameDetails ||
      null
    );
  }

  get title(): string {
    return (
      this.tournament?.name ||
      this.tournamentDetails?.name ||
      this.gameDetails?.name ||
      ''
    );
  }

  get subtitle(): string {
    if (this.tournament) {
      return this.tournament.description;
    }

    if (this.tournamentDetails) {
      const tournamentDescription =
        this.tournamentDetails.description ||
        this.tournamentDetails.metadata?.gameDescriptionShort ||
        '';

      return tournamentDescription as string;
    }

    if (this.gameDetails) {
      return this.gameDetails.description;
    }

    return '';
  }

  get metadata(): Metadata | null {
    return (
      this.tournament?.metadata ?? this.tournamentDetails?.metadata ?? null
    );
  }

  get startTime(): Date | null | undefined {
    if (this.tournament) {
      if (typeof this.tournament.casualGameStartTime === 'string') {
        return new Date(this.tournament.casualGameStartTime);
      }
      return this.tournament.casualGameStartTime;
    }

    if (this.tournamentDetails && this.tournamentDetails.casualGameStartTime) {
      return new Date(this.tournamentDetails.casualGameStartTime);
    }

    return null;
  }

  get endTime(): Date | null | undefined {
    if (this.tournament) {
      return this.tournament.casualGameEndTime;
    }

    if (this.tournamentDetails && this.tournamentDetails.lifeExperience) {
      return new Date(this.tournamentDetails.casualGameEndTime);
    }

    return null;
  }

  get prizeDetail(): PrizeDetails | null | undefined {
    if (this.tournament) {
      return this.tournament.prizeDetails;
    }

    if (this.tournamentDetails) {
      if (this.tournamentDetails.lifeExperience) {
        const prizeDetails: PrizeDetails[] =
          this.tournamentDetails?.detailsForEventSchedule.reduce(
            (acc: any[], curr: EventSchedule) => acc.concat(curr.prizeDetails),
            [],
          );
        const prize = prizeDetails.find((p) => p.rank === 1);

        return prize as any;
      }
    }

    return null;
  }

  get entryAmount(): number {
    if (this.eventScheduler) {
      return this.eventScheduler.entryAmount;
    }

    if (this.tournament?.lifeExperience) {
      return this.tournament.eventSchedules[0].entryAmount;
    }

    if (this.tournamentDetails?.lifeExperience) {
      const event = this.tournamentDetails.detailsForEventSchedule.find((e) => {
        const prize = e.prizeDetails.find(
          (p) => p.prizeType === 'LIFE_EXPERIENCE',
        );
        if (prize) {
          return true;
        }

        return false;
      });

      if (event) {
        return event.entryAmount;
      } else {
        console.error('Tournament error data');
      }
    }

    return 0.0;
  }

  get actionBarOptions(): ActionBarConfig {
    return {
      showShareButton: true,
      ...this.actionBarConfig,
    };
  }

  get isComingSoon(): boolean {
    if (this.startTime && this.startTime.getTime() > Date.now()) {
      return true;
    }

    return false;
  }

  get isZboOnly(): boolean {
    return !!(
      this.tournament?.metadata?.tags?.includes('ZBO') ||
      this.tournamentDetails?.metadata?.tags?.includes('ZBO')
    );
  }

  get isLifeExperience(): boolean {
    return (
      this.tournament?.lifeExperience ||
      this.tournamentDetails?.lifeExperience ||
      false
    );
  }

  get isEnded(): boolean {
    if (this.endTime) {
      const date =
        typeof this.endTime === 'string'
          ? new Date(this.endTime)
          : this.endTime;
      if (date.getTime() < Date.now()) {
        return true;
      }
    }

    return false;
  }

  get vipVideo(): string | null | string[] {
    if (this.tournament) {
      if (
        this.tournament.lifeExperience &&
        this.tournament.metadata &&
        this.tournament.metadata.vipVideo
      ) {
        return this.tournament.metadata.vipVideo as string | null;
      }
    }

    if (this.tournamentDetails) {
      if (
        this.tournamentDetails.lifeExperience &&
        this.tournamentDetails.metadata &&
        this.tournamentDetails.metadata.vipVideo
      ) {
        return this.tournamentDetails.metadata.vipVideo as string;
      }
    }

    return null;
  }

  onJoinButtonPress() {
    this.joinPress.emit();
  }

  onPlayButtonPress() {
    this.playPress.emit(this.passForTournament as TournamentPass);
  }

  onShareButtonPress() {
    this.sharePress.emit();
  }

  onTryForFreePress() {
    this.tryForFreePress.emit();
  }

  onPlayAnotherPress() {
    this.playAnotherPress.emit();
  }

  onEvent(event: any) {
    //
  }

  openPromotionalVideo() {
    this.dialogService.open(DialogVideoComponent, {
      minWidth: '300px',
      data: {
        videoUrl: this.vipVideo,
      },
    });
  }
}
